<script setup>
import { router } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'

/**
 * RED-384: Seadete versioonid. Newest first: vN, date · author, the note,
 * `kehtiv` or how many checks used it, and what changed against the version
 * before. `Taasta see versioon` saves the old values as a NEW version.
 */
defineProps({
  versions: { type: Array, default: undefined },
})

const visible = defineModel('visible', { type: Boolean, default: false })

const { t } = useI18n()
const toast = useToast()
const confirm = useConfirm()

const dateTime = iso =>
  iso ? new Intl.DateTimeFormat('et-EE', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso)) : ''

const fieldLabel = change => {
  if (change.key === 'prices') {
    return t('messages.admin.report_qc.history.prices_field', {
      field: t(`messages.admin.report_qc.settings.price_${change.field}`),
      model: change.model,
    })
  }
  return t(`messages.admin.report_qc.settings.${change.key}`)
}

const formatValue = (key, value) => {
  if (value === null || value === undefined || value === '') {
    return '—'
  }
  if (key === 'thinking_level') {
    return t(`messages.admin.report_qc.settings.thinking.${value}`)
  }
  if (key === 'prices_checked_on') {
    const [year, month, day] = String(value).split('-')
    return `${day}.${month}.${year}`
  }
  if (typeof value === 'number') {
    return new Intl.NumberFormat('et-EE', { maximumFractionDigits: 6 }).format(value)
  }
  return String(value)
}

const restore = version => {
  confirm.require({
    header: t('messages.admin.report_qc.history.confirm_header', { n: version.number }),
    message: t('messages.admin.report_qc.history.confirm_message'),
    icon: 'pi pi-history',
    rejectProps: { label: t('messages.admin.report_qc.history.confirm_reject'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('messages.admin.report_qc.history.confirm_accept') },
    accept: () => {
      router.post(
        route('admin.report-qc.settings.restore', version.id),
        {},
        {
          preserveScroll: true,
          onSuccess: page => {
            toast.add({
              severity: 'success',
              summary: t('messages.admin.report_qc.history.restored'),
              detail: t('messages.admin.report_qc.history.restored_detail', {
                old: version.number,
                new: page.props.settings.versionNumber,
              }),
              life: 4000,
            })
          },
          onError: errors => {
            toast.add({ severity: 'error', summary: t('messages.error'), detail: errors.restore, life: 6000 })
          },
        },
      )
    },
  })
}
</script>

<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    :header="t('messages.admin.report_qc.history.title')"
    class="w-full! md:w-[32rem]!"
    data-testid="settings-history"
  >
    <div v-if="versions === undefined" class="flex flex-col gap-3">
      <Skeleton v-for="n in 3" :key="n" height="5rem" />
    </div>

    <p v-else-if="versions.length === 0" class="text-sm text-gray-600">
      {{ t('messages.admin.report_qc.settings.no_version_yet') }}
    </p>

    <ol v-else class="flex flex-col gap-4">
      <li
        v-for="version in versions"
        :key="version.id"
        class="rounded-md border border-gray-200 p-3"
        :data-testid="`settings-history-v${version.number}`"
      >
        <div class="flex flex-wrap items-center gap-2">
          <span class="font-mono font-medium">v{{ version.number }}</span>
          <Tag v-if="version.current" :value="t('messages.admin.report_qc.history.current')" severity="success" />
          <span class="text-xs text-gray-500">{{ t('messages.admin.report_qc.history.used', { n: version.usedInChecks }) }}</span>
        </div>
        <p class="mt-1 text-xs text-gray-500">
          {{ dateTime(version.createdAt) }} · {{ version.author ?? t('messages.admin.report_qc.history.author_system') }}
        </p>
        <p class="mt-2 text-sm whitespace-pre-line">{{ version.note }}</p>

        <p v-if="version.changes === null" class="mt-2 text-xs text-gray-500">{{ t('messages.admin.report_qc.history.first_version') }}</p>
        <p v-else-if="version.changes.length === 0" class="mt-2 text-xs text-gray-500">{{ t('messages.admin.report_qc.history.no_changes') }}</p>
        <ul v-else class="mt-2 flex flex-col gap-1 text-sm">
          <li v-for="change in version.changes" :key="`${change.key}-${change.model}-${change.field}`">
            <span class="text-gray-600">{{ fieldLabel(change) }}:</span>
            <span class="ml-1 text-gray-500 line-through">{{ formatValue(change.key, change.old) }}</span>
            <i class="pi pi-arrow-right mx-1 text-xs text-gray-400" aria-hidden="true" />
            <span class="font-medium">{{ formatValue(change.key, change.new) }}</span>
          </li>
        </ul>

        <div v-if="!version.current" class="mt-3 flex justify-end">
          <Button
            :label="t('messages.admin.report_qc.history.restore')"
            icon="pi pi-history"
            size="small"
            severity="secondary"
            outlined
            :data-testid="`settings-restore-v${version.number}`"
            @click="restore(version)"
          />
        </div>
      </li>
    </ol>
  </Drawer>
</template>
